import { stringify } from "qs";

import axios from "axios";
import deepmerge from "deepmerge";

import { getDeviceUUID, getJwtToken, getUserSession, setJwtToken } from "./localstorage";
import { actiDomain, apiDomain, DEVICE_UUID_HEADER } from "./constants";
import { router } from "./router";
import authService from "./auth.service";

const api = axios.create();

// Adding user request token
api.interceptors.request.use(
  async config => {
    const isLoginEndpoint = config.url.includes("/auth/login");

    if (isLoginEndpoint) {
      return config;
    }

    const token = getJwtToken();

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
);

// Adding device uuid
api.interceptors.request.use(
  async config => {
    const deviceUUID = getDeviceUUID();

    if (deviceUUID) {
      config.headers[DEVICE_UUID_HEADER] = deviceUUID;
    }

    return config;
  },
);

// Adding parameter serializer
api.interceptors.request.use(config => {
  const isGLPIEndpoint = config.url.includes(actiDomain);
  const isLoginEndpoint = config.url.includes("/auth/login");
  const isRefreshEndpoint = config.url.includes("/auth/refresh");

  if (isLoginEndpoint || isRefreshEndpoint) {
    return config;
  }
  
  if (isGLPIEndpoint) {
    return deepmerge(config, {
      paramsSerializer: {
        indexes: true,
      }
    });
  }

  try {
    const { activeentity } = getUserSession() || {};

    return {
      paramsSerializer: {
        serialize: arrayParamSerializer,
      },
      ...config,
      params: {
        entityId: activeentity,
        ...config.params,
      },
    };
  } catch (err) {
    console.error(err);
    return config;
  }
});

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeToRefresh = callback => refreshSubscribers.push(callback);
const onRefreshed = token => refreshSubscribers.forEach(callback => callback.apply(token));

// Response interceptor
api.interceptors.response.use(
  undefined,
  error => {
    const originalRequest = error.config;
    const isUnauthorized = error.response.status === 401;
    const isGLPIRequest = originalRequest.url.includes(actiDomain);

    const isForbidden = isUnauthorized
      && isGLPIRequest
      && error.response.data
      && error.response.data[0] === "ERROR_RIGHT_MISSING";

    if (isForbidden) {
      error.response.status = 403;
      error.response.statusText = "Forbidden";
      return Promise.reject(error);
    }

    const refreshDenied = isUnauthorized && originalRequest.url.includes("/auth/refresh");

    if (refreshDenied) {
      authService.logout();
      router.replace("/login");
      return Promise.reject(error);
    }

    const shouldRetry = isUnauthorized && !originalRequest._retry && !originalRequest.url.includes("/auth/login");

    if (!shouldRetry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise(resolve => {
        subscribeToRefresh(() => {
          resolve(api(originalRequest));
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    return api
      .get(`${apiDomain}/auth/refresh`, { withCredentials: true })
      .then(res => {
        if (res.status === 200) {
          let { accessToken, expiresIn } = res.data;
          if (!accessToken && !expiresIn) {
            accessToken = getJwtToken();
          }

          setJwtToken(accessToken);

          isRefreshing = false;
          onRefreshed(accessToken);
          refreshSubscribers = [];

          return api(originalRequest);
        }

        return Promise.reject();
      })
      .catch(err => {
        isRefreshing = false;
        refreshSubscribers = [];
        throw err;
      });
  },
);

export const arrayParamSerializer = params => stringify(params, { arrayFormat: "comma" });

export default api;
